import { EMPTY, merge, of } from 'rxjs';
import {
    catchError,
    last,
    map,
    share,
    skipWhile,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs/operators';
import $ from '../jquery';
import { fromEvents } from '../rxjs';
import Sentry from '../sentry';
import EventEmitter from './eventEmitter';
import Point from './point';

/**
 * Event handler for dragging items. The main task of this
 * object is the delegation of drag and drop events for a given element.
 *
 *     var dragAndDrop = new Ofcs.Draggable(element);
 *
 *     dragAndDrop.on('dragStart', function(e) {
 *         // ...
 *     });
 *     dragAndDrop.on('drag', function(e) {
 *         // ...
 *     });
 *     dragAndDrop.on('dragStop', function(e) {
 *         // ...
 *     });
 *
 * @class Draggable
 * @extends EventEmitter
 */
export default class Draggable extends EventEmitter {
    /**
     * The `dragStart` event is triggered when the user starts dragging
     *
     * @event dragStart
     * @param {Object} event Event object for drag event. See #drag for details.
     */

    /**
     * The `drag` event is triggered when a drag commenced
     *
     * @event drag
     * @param {Object} event Event object for drag event. See #drag for details.
     */

    /**
     * The `dragStop` event is triggered when dragging was stopped
     *
     * @event dragStop
     * @param {Object} event Event object for drag event. See #drag for details.
     */

    /**
     * Threshold in pixels before dragging starts
     *
     * @protected
     * @property {Number} [threshold=0]
     */
    threshold = 0;

    subscription = null;

    /**
     * Creates a new drag-and-drop handler for the given element
     *
     * @constructor
     * @param {HTMLElement|SVGElement|jQuery} element Element to bind the dragstart on
     * @param {string} selector A selector string to filter the descendants of the selected elements
     *                          that trigger the event. If the selector is null or omitted, the event
     *                          is always triggered when it reaches the selected element.
     */
    constructor(element) {
        super();

        // ensure basic DOM element
        this.element = element instanceof $ ? element.get(0) : element;

        const drag$ = this.createStartObservable(element).pipe(
            switchMap(startEvent => this.createDragObservable(startEvent)),
            catchError(this.catchToSentry),
        );

        // TODO: Find a way to unsubscribe, but dragAndDrop is usually instantiated once and never destroyed anyway
        this.subscription = drag$.subscribe((event) => {
            this.trigger(event.type, event);
        });
    }

    destroy() {
        this.subscription.unsubscribe();
    }

    createStartObservable(element) {
        return fromEvents(element, 'mousedown', 'touchstart').pipe(
            tap(this.stopEventPropagation),
        );
    }

    createDragObservable(originalStartEvent) {
        const startEvent = {
            target: originalStartEvent.target,
            pageX: originalStartEvent.pageX,
            pageY: originalStartEvent.pageY,
        };

        const baseMove$ = fromEvents(document, 'mousemove').pipe(
            tap(this.stopEventPropagation),
            takeUntil(this.createEndObservable()),
            map(moveEvent => ({ startEvent, moveEvent })),
            skipWhile(events => !this.hasMovedByThreshold(events)),
            share(),
        );

        // Trigger dragStart event on first move event
        const first$ = baseMove$.pipe(
            take(1), // Get the first move event
            switchMap(events => this.transformToEvent('dragStart', events)),
        );

        // Trigger drag event on every move event
        const move$ = baseMove$.pipe(
            switchMap(events => this.transformToEvent('drag', events)),
        );

        // Trigger dragStop event after last move event
        const last$ = baseMove$.pipe(
            last(value => !!value, null), // Get the last move event (or default to null)
            switchMap(events => this.transformToEvent('dragStop', events)),
        );

        return merge(first$, move$, last$).pipe(
            catchError(this.catchToSentry),
        );
    }

    createEndObservable() {
        return fromEvents(document, 'mouseup', 'touchend', 'click').pipe(
            tap(this.stopEventPropagation),
        );
    }

    stopEventPropagation(event) {
        event.stopPropagation();
        event.preventDefault();
    }

    transformToEvent(type, events) {
        if (events) return of(this.createEvent(type, events));

        return EMPTY;
    }

    hasMovedByThreshold({ startEvent, moveEvent }) {
        const start = new Point(startEvent.pageX, startEvent.pageY);
        const move = new Point(moveEvent.pageX, moveEvent.pageY);

        const distance = Math.abs(start.getDistanceTo(move));

        return distance >= this.threshold;
    }

    triggerStartEventOnFirstValidMovement(previousEvents, events) {
        if (!previousEvents) this.triggerDragEvent('dragStart', events);

        return events;
    }

    /**
     * Set threshold in pixels before dragging starts.
     *
     * @param {number} threshold
     */
    setThreshold(threshold) {
        this.threshold = threshold;
    }

    /**
     * @protected
     * @param {string} type The triggered which will be triggered
     * @param {Object} events Event object from the listened DOM event
     * @return {jQuery.Event}
     */
    createEvent(type, { startEvent, moveEvent }) {
        const event = $.Event(type);

        event.startX = startEvent.pageX;
        event.startY = startEvent.pageY;
        event.startTarget = startEvent.target.correspondingUseElement || startEvent.target;

        event.pageX = moveEvent.pageX;
        event.pageY = moveEvent.pageY;
        event.offsetX = event.startX - moveEvent.pageX;
        event.offsetY = event.startY - moveEvent.pageY;

        event.originalEvent = moveEvent;
        event.target = moveEvent.target.correspondingUseElement || moveEvent.target;

        return event;
    }

    catchToSentry(error) {
        Sentry.captureException(error);

        return EMPTY;
    }
}
